import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Title3, Spinner, Text, Button, makeStyles, tokens } from "@fluentui/react-components";
import {
  Add20Regular,
  ArrowSync20Regular,
  CheckmarkCircle20Regular,
} from "@fluentui/react-icons";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import {
  getStatusItemsBySpreadsheet,
  deleteStatusItem,
  updateStatusItemStatus,
  updateStatusItemName,
  updateStatusItemVisibility,
  updateStatusItemDocument,
  StatusItemDto,
} from "../../services/status-items.service";
import { getWorkpaperBalances, WorkpaperBalanceDto } from "../../services/xero-integration.service";
import { getUserFriendlyErrorMessage } from "../../utils/error-messages";
import { getLowestStatus } from "../../utils/statusItemHelpers";
import { StatusStatistics } from "./components/StatusStatistics";
import { StatusFilters } from "./components/StatusFilters";
import { StatusItemTable } from "./components/StatusItemTable";
import { EmptyState } from "./components/EmptyState";
import CreateStatusItemDialog from "./components/CreateStatusItemDialog";
import BulkReviewDialog from "../../components/BulkReviewDialog";
import { ExcelService } from "../../services/excel.service";
import { syncWorksheetsToStatusItems } from "../../services/sync-worksheets.service";
import { useTrackEvent } from "../../hooks/useTrackEvent";
import { ribbonService } from "../../services/ribbon.service";
import { useTypographyStyles } from "../../styles/typography.styles";

const useStyles = makeStyles({
  container: {
    padding: tokens.spacingVerticalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
    height: "100%",
    // Page itself doesn't scroll — header, stats, and filters stay pinned and
    // the StatusItemTable below claims the remaining height + scrolls inside.
    overflow: "hidden",
    boxSizing: "border-box",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: tokens.spacingVerticalM,
  },
  headerActions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacingVerticalXXXL,
  },
  errorContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    textAlign: "center",
    color: tokens.colorPaletteRedForeground1,
  },
});

/**
 * Status-item table for the workbook — filters, sorting, sync-from-Excel,
 * sign-off dialogs, and Xero-sourced workpaper balances per item.
 */
const WorkflowStatus: React.FC = () => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const track = useTrackEvent();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { spreadsheetId, userRole, refreshWorksheets, worksheets, workbookSetup, isStandalone } = useWorkflowIntelligence();

  const [statusItems, setStatusItems] = useState<StatusItemDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isBulkReviewDialogOpen, setIsBulkReviewDialogOpen] = useState(false);
  const [workpaperBalances, setWorkpaperBalances] = useState<Map<string, WorkpaperBalanceDto>>(new Map());

  // Filter state
  const [search, setSearch] = useState(() => {
    const urlSearch = searchParams.get("search");
    return urlSearch ? decodeURIComponent(urlSearch) : "";
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    const urlStatus = searchParams.get("status");
    return urlStatus ? decodeURIComponent(urlStatus) : "all";
  });

  // Sort state - default to sheet order (matching Excel's tab order)
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "sheetOrder",
    direction: "asc",
  });

  // Show hidden items toggle
  const [showHidden, setShowHidden] = useState(false);

  // Load status items
  const loadStatusItems = React.useCallback(async () => {
    if (!spreadsheetId) {
      setError("No spreadsheet ID available");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getStatusItemsBySpreadsheet(spreadsheetId);
      setStatusItems(data);
    } catch (err) {
      const friendlyMessage = getUserFriendlyErrorMessage(err);
      setError(friendlyMessage);
    } finally {
      setIsLoading(false);
    }
  }, [spreadsheetId]);

  useEffect(() => {
    loadStatusItems();
  }, [loadStatusItems]);

  // Refresh whenever any other part of the app signals that status items have
  // changed — fired by BulkReviewDialog regardless of which instance ran it
  // (the in-page button or the Excel ribbon action via MainLayout).
  useEffect(() => {
    const handler = () => {
      loadStatusItems();
      ribbonService.updateRibbonStatusIndicator();
    };
    window.addEventListener("statusItemsChanged", handler);
    return () => window.removeEventListener("statusItemsChanged", handler);
  }, [loadStatusItems]);

  // Load workpaper balances from Xero (if connected)
  const hasXeroConnection = !!workbookSetup?.xeroTenantId;

  useEffect(() => {
    const loadWorkpaperBalances = async () => {
      if (!spreadsheetId || !hasXeroConnection) {
        setWorkpaperBalances(new Map());
        return;
      }

      try {
        const balances = await getWorkpaperBalances(spreadsheetId);
        // Create lookup map by BdoWorkpaperCode
        const balanceMap = new Map<string, WorkpaperBalanceDto>();
        balances.forEach((b) => balanceMap.set(b.bdoWorkpaperCode, b));
        setWorkpaperBalances(balanceMap);
      } catch (err) {
        console.error("Failed to load workpaper balances:", err);
        // Don't show error to user - balances are supplementary data
        setWorkpaperBalances(new Map());
      }
    };

    loadWorkpaperBalances();
  }, [spreadsheetId, hasXeroConnection]);

  // Calculate statistics (excluding hidden items)
  const statistics = useMemo(() => {
    const visibleItems = statusItems.filter((item) => !item.isHidden);
    return {
      open: visibleItems.filter((item) => !item.status || item.status === "Open").length,
      processed: visibleItems.filter((item) => item.status === "Processed").length,
      reworkRequired: visibleItems.filter((item) => item.status === "Rework Required").length,
      qualityReviewed: visibleItems.filter((item) => item.status === "Quality Reviewed").length,
      partnerReviewed: visibleItems.filter((item) => item.status === "Partner Reviewed").length,
    };
  }, [statusItems]);

  // Count of hidden items
  const hiddenCount = useMemo(() => {
    return statusItems.filter((item) => item.isHidden).length;
  }, [statusItems]);

  // Apply filters and sorting
  const filteredAndSortedItems = useMemo(() => {
    let result = [...statusItems];

    // Filter hidden items unless showHidden is true
    if (!showHidden) {
      result = result.filter((item) => !item.isHidden);
    }

    // Search filter
    if (search) {
      const lowerSearch = search.toLowerCase();
      result = result.filter(
        (item) =>
          item.name.toLowerCase().includes(lowerSearch) ||
          item.preparerName?.toLowerCase().includes(lowerSearch) ||
          item.reviewerName?.toLowerCase().includes(lowerSearch) ||
          item.partnerName?.toLowerCase().includes(lowerSearch)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      if (statusFilter === "has_discussions") {
        // Special filter for items with discussions
        result = result.filter((item) => item.discussionCount > 0);
      } else {
        result = result.filter((item) => item.status === statusFilter);
      }
    }

    // Sorting
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortConfig.key) {
        case "sheetOrder": {
          // Sort by Excel sheet order - items without sheets go to the end
          const aIndex = a.sheetId ? worksheets.findIndex(ws => ws.id === a.sheetId) : -1;
          const bIndex = b.sheetId ? worksheets.findIndex(ws => ws.id === b.sheetId) : -1;
          // Items not linked to a sheet (index -1) should be at the end
          const aOrder = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
          const bOrder = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
          comparison = aOrder - bOrder;
          break;
        }
        case "name":
          comparison = a.name.localeCompare(b.name);
          break;
        case "status":
          comparison = (a.status || "").localeCompare(b.status || "");
          break;
        case "discussionCount":
          comparison = a.discussionCount - b.discussionCount;
          break;
        case "lastUpdated":
          comparison =
            new Date(a.lastUpdated).getTime() - new Date(b.lastUpdated).getTime();
          break;
        default:
          break;
      }

      return sortConfig.direction === "asc" ? comparison : -comparison;
    });

    return result;
  }, [statusItems, search, statusFilter, sortConfig, showHidden, worksheets]);

  // Handle sync with Excel
  const handleSyncExcel = async () => {
    if (!spreadsheetId) {
      setError("No spreadsheet ID available");
      return;
    }
    // Excel-only — the button is hidden in standalone, but if this ever gets
    // wired to another trigger, syncWorksheetsToStatusItems would attempt
    // to call Excel. It self-gates on isStandaloneMode but this second
    // guard keeps the loading state honest.
    if (isStandalone) return;

    setIsSyncing(true);
    setError(null);

    try {
      const updatedItems = await syncWorksheetsToStatusItems(spreadsheetId);
      setStatusItems(updatedItems);
      await refreshWorksheets();
    } catch (err) {
      const friendlyMessage = getUserFriendlyErrorMessage(err);
      setError(friendlyMessage);
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (itemId: number, newStatus: string) => {
    // Snapshot from/type from local state BEFORE the update lands, so the
    // emitted event reflects the transition rather than the post-state.
    const previous = statusItems.find((i) => i.id === itemId);
    try {
      const updatedItem = await updateStatusItemStatus(itemId, newStatus);
      track("status_item.status_changed", {
        from: previous?.status,
        to: newStatus,
        type: previous?.type,
      });
      // Update the item in the local state
      setStatusItems((prevItems) =>
        prevItems.map((item) => (item.id === itemId ? updatedItem : item))
      );
      // Update tab colour for the specific worksheet that was changed.
      // Skipped in standalone — ExcelService.getWorksheetNameById calls
      // Excel.run which is undefined outside the add-in host and throws
      // "Excel is not defined". The subsequent ribbon call is a no-op in
      // standalone anyway (ribbonService self-gates on isStandaloneMode),
      // but this ExcelService call is not, so we gate it explicitly.
      if (updatedItem.sheetId && !isStandalone) {
        // Get all statuses for this worksheet (with the updated item reflected)
        const worksheetStatuses = statusItems
          .filter((item) => item.sheetId === updatedItem.sheetId)
          .map((item) => (item.id === itemId ? newStatus : item.status));
        const lowestStatus = getLowestStatus(worksheetStatuses);
        // Resolve sheet ID to name for tab color update
        const sheetName = await ExcelService.getWorksheetNameById(updatedItem.sheetId);
        await ribbonService.updateTabColorForWorksheet(sheetName, lowestStatus);
      }
      // Update ribbon for active worksheet (in case it's the one that changed)
      // ribbonService self-gates on isStandaloneMode, so safe to call.
      await ribbonService.updateRibbonStatusIndicator();
    } catch (err) {
      const friendlyMessage = getUserFriendlyErrorMessage(err);
      setError(friendlyMessage);
    }
  };

  // Handle name change
  const handleNameChange = async (itemId: number, newName: string) => {
    try {
      const updatedItem = await updateStatusItemName(itemId, newName);
      setStatusItems((prevItems) =>
        prevItems.map((item) => (item.id === itemId ? updatedItem : item))
      );
    } catch (err) {
      const friendlyMessage = getUserFriendlyErrorMessage(err);
      setError(friendlyMessage);
    }
  };

  // Handle delete
  const handleDelete = async (itemId: number) => {
    try {
      await deleteStatusItem(itemId);
      // Remove the item from the local state
      setStatusItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
    } catch (err) {
      const friendlyMessage = getUserFriendlyErrorMessage(err);
      setError(friendlyMessage);
    }
  };

  // Handle visibility toggle
  const handleToggleVisibility = async (itemId: number, isHidden: boolean) => {
    try {
      const updatedItem = await updateStatusItemVisibility(itemId, isHidden);
      // Update the item in the local state
      setStatusItems((prevItems) =>
        prevItems.map((item) => (item.id === itemId ? updatedItem : item))
      );
    } catch (err) {
      const friendlyMessage = getUserFriendlyErrorMessage(err);
      setError(friendlyMessage);
    }
  };

  const handleDocumentChange = async (itemId: number, documentId: string | null) => {
    try {
      const updatedItem = await updateStatusItemDocument(itemId, documentId);
      setStatusItems((prevItems) =>
        prevItems.map((item) => (item.id === itemId ? updatedItem : item))
      );
    } catch (err) {
      const friendlyMessage = getUserFriendlyErrorMessage(err);
      setError(friendlyMessage);
    }
  };

  // Handle sort
  const handleSort = (key: string) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Handle status filter from statistics cards
  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setSearch("");
    setStatusFilter("all");
  };

  // Handle discussion count click - navigate to discussions page with status item filter
  const handleDiscussionClick = (item: StatusItemDto) => {
    navigate(`/discussions?statusItemId=${item.id}`);
  };

  // Handle create dialog success
  const handleCreateSuccess = async () => {
    if (!spreadsheetId) return;
    try {
      const data = await getStatusItemsBySpreadsheet(spreadsheetId);
      setStatusItems(data);
    } catch (err) {
      console.error("Error refreshing status items:", err);
    }
  };

  // Check if filters are active
  const hasActiveFilters = search !== "" || statusFilter !== "all";

  // Render loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <Spinner size="large" label="Loading status items..." />
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Title3>Workflow Status</Title3>
        </div>
        <div className={styles.errorContainer}>
          <Text className={typography.emptyStateTitle}>
            Error loading status items
          </Text>
          <Text>{error}</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <Title3>Workflow Status</Title3>
        <div className={styles.headerActions}>
          {/* Sync Excel is Excel-only — reads worksheets from the workbook to
              reconcile status items. Standalone has no workbook to sync from,
              so we hide the button entirely rather than showing it disabled. */}
          {!isStandalone && (
            <Button
              appearance="subtle"
              icon={<ArrowSync20Regular />}
              onClick={handleSyncExcel}
              disabled={isSyncing || isLoading}
            >
              {isSyncing ? "Syncing..." : "Sync Excel"}
            </Button>
          )}
          <Button
            appearance="secondary"
            icon={<CheckmarkCircle20Regular />}
            onClick={() => setIsBulkReviewDialogOpen(true)}
          >
            Sign Off Sheet
          </Button>
          <Button
            appearance="primary"
            icon={<Add20Regular />}
            onClick={() => setIsCreateDialogOpen(true)}
          >
            Add Item
          </Button>
        </div>
      </div>

      {/* Statistics Cards */}
      <StatusStatistics
        openCount={statistics.open}
        processedCount={statistics.processed}
        reworkRequiredCount={statistics.reworkRequired}
        qualityReviewedCount={statistics.qualityReviewed}
        partnerReviewedCount={statistics.partnerReviewed}
        onStatusClick={handleStatusFilter}
      />

      {/* Filters */}
      <StatusFilters
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        showHidden={showHidden}
        onShowHiddenChange={setShowHidden}
        resultCount={filteredAndSortedItems.length}
        hiddenCount={hiddenCount}
        onClearFilters={handleClearFilters}
      />

      {/* Table or Empty State */}
      {filteredAndSortedItems.length === 0 ? (
        <EmptyState hasFilters={hasActiveFilters} onClearFilters={handleClearFilters} />
      ) : (
        <StatusItemTable
          items={filteredAndSortedItems}
          workpaperBalances={workpaperBalances}
          sortConfig={sortConfig}
          onSort={handleSort}
          userRole={userRole}
          onStatusChange={handleStatusChange}
          onNameChange={handleNameChange}
          onDelete={handleDelete}
          onDiscussionClick={handleDiscussionClick}
          onToggleVisibility={handleToggleVisibility}
          onDocumentChange={handleDocumentChange}
          clientId={workbookSetup?.clientId}
          onRefresh={loadStatusItems}
        />
      )}

      {/* Create Status Item Dialog */}
      {spreadsheetId && (
        <CreateStatusItemDialog
          isOpen={isCreateDialogOpen}
          onDismiss={() => setIsCreateDialogOpen(false)}
          spreadsheetId={spreadsheetId}
          onSuccess={handleCreateSuccess}
          existingStatusItems={statusItems}
        />
      )}

      {/* Bulk Review Dialog */}
      <BulkReviewDialog
        isOpen={isBulkReviewDialogOpen}
        onDismiss={() => setIsBulkReviewDialogOpen(false)}
        onSuccess={async () => {
          await loadStatusItems();
          await ribbonService.updateRibbonStatusIndicator();
        }}
      />
    </div>
  );
};

export default WorkflowStatus;
